import React from 'react';
import { Box, TextLink, Typography } from '@drone-js/core';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import AgodaMarkdown from 'component/common/AgodaMarkdown/AgodaMarkdown';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesReviewCardTruncateTextlinkClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesReviewCardTruncateTextlinkClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { TextType, TypographyType } from '@drone-js/theme';
import styled from 'styled-components';
import { StringFormatter } from '@agoda/react-handyman';

const TextLinkStyled = styled(TextLink)`
    span {
        color: inherit;
        text-decoration-line: underline;
    }
`;

interface OwnProps {
    text: string;
    defaultExpanded: boolean;
    threshold?: number;
    elementName?: string;
    toggleButtonElementName?: string;
    isModal?: boolean;
    textVariant?: TypographyType;
    textColor?: TextType;
    onToggleOverride?: () => void;
    isTextLinkInheritColor?: boolean;
    title?: string;
}

type Props = OwnProps;

const TEXT_ELLIPSIS = '...';
const DEFAULT_THRESHOLD = 200;
const DEFAULT_TEXT_VARIANT = 'body-s';
const DEFAULT_TEXT_COLOR = 'base.default';

const TextToggler: React.FC<Props> = (props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const [isExpanded, setIsExpanded] = React.useState(props.defaultExpanded);
    const threshold = props.threshold ?? DEFAULT_THRESHOLD;
    const textVariant = props.textVariant ?? DEFAULT_TEXT_VARIANT;
    const textColor = props.textColor ?? DEFAULT_TEXT_COLOR;

    const onToggle = () => {
        trackElementEvent<ActivitiesReviewCardTruncateTextlinkClickEventActivitiesFieldsV1>(ActivitiesReviewCardTruncateTextlinkClickEventActivitiesManifestV1.ids, {
            is_modal: props.isModal ?? false,
        });
        setIsExpanded(!isExpanded);
    };

    if (props.text.length < threshold) {
        return (
            <Box mt="8" data-testid={props.elementName}>
                <Typography variant={textVariant} textColor={textColor}>
                    <AgodaMarkdown input={props.text} />
                </Typography>
            </Box>
        );
    }

    const textShort = props.text.substring(0, threshold).concat(TEXT_ELLIPSIS);
    const onToggleOverride = props.onToggleOverride ?? onToggle;
    const ariaLabel = props.title && StringFormatter.format(cms[321441], !isExpanded ? cms[126849] : cms[130714], props.title);

    return (
        <Box mt="8" data-testid={props.elementName}>
            <Typography variant={textVariant} textColor={textColor}>
                <AgodaMarkdown inline={true} input={!isExpanded ? textShort : props.text} />
                {props.isTextLinkInheritColor ? (
                    // eslint-disable-next-line @agoda/agoda-a11y/no-non-drone-interactable -- @agoda/agoda-a11y/no-non-drone-interactable[5741fb11-ad23-4884-bef2-a307d3089591]
                    <TextLinkStyled
                        label={!isExpanded ? cms[126849] : cms[130714]}
                        onClick={onToggleOverride}
                        data-testid={props.toggleButtonElementName}
                        textVariant={textVariant}
                        aria-label={ariaLabel}
                    />
                ) : (
                    <TextLink
                        hierarchy="primary"
                        label={!isExpanded ? cms[126849] : cms[130714]}
                        onClick={onToggleOverride}
                        data-testid={props.toggleButtonElementName}
                        textVariant={textVariant}
                        aria-label={ariaLabel}
                    />
                )}
            </Typography>
        </Box>
    );
};

TextToggler.displayName = 'TextToggler';
export default TextToggler;
